export interface ParsedChatEntry {
  id: string;
  role: "user" | "bot";
  text?: string;
  reply_lines?: string[];
  tools_used?: string[];
  timestamp: Date;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  const single = asString(value);
  return single ? [single] : [];
}

function pickString(obj: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = asString(obj[key]);
    if (value) return value;
  }
  return "";
}

function pickDate(obj: Record<string, unknown>): Date {
  const raw =
    obj.create_on ??
    obj.created_on ??
    obj.created_at ??
    obj.created ??
    obj.timestamp ??
    obj.updated_at;
  if (typeof raw === "string" || typeof raw === "number") {
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return new Date();
}

function pickId(obj: Record<string, unknown>, fallback: string): string {
  const id = obj.id ?? obj.uuid ?? obj.pk;
  return id != null && String(id) ? String(id) : fallback;
}

function roleOf(obj: Record<string, unknown>): "user" | "bot" | null {
  const role = asString(obj.role ?? obj.sender ?? obj.type).toLowerCase();
  if (role === "user" || role === "human") return "user";
  if (role === "bot" || role === "assistant" || role === "ai" || role === "system") {
    return "bot";
  }
  return null;
}

function replyLinesOf(obj: Record<string, unknown>): string[] {
  const lines = asStringArray(obj.reply_lines);
  if (lines.length) return lines;
  const reply = pickString(obj, ["reply", "answer", "assistant_message", "response"]);
  return reply ? [reply] : [];
}

function toolsOf(obj: Record<string, unknown>): string[] | undefined {
  const tools = asStringArray(obj.tools_used).length
    ? asStringArray(obj.tools_used)
    : asStringArray(obj.tools);
  return tools.length ? tools : undefined;
}

function unwrapList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!isRecord(data)) return [];

  for (const key of [
    "results",
    "data",
    "messages",
    "history",
    "chats",
    "items",
    "conversation",
    "conversations",
  ]) {
    const value = data[key];
    if (Array.isArray(value)) return flattenNested(value);
    if (isRecord(value)) {
      const nested = unwrapList(value);
      if (nested.length) return nested;
    }
  }

  if (roleOf(data) || pickString(data, ["message", "query", "prompt", "user_message", "question"])) {
    return [data];
  }
  return [];
}

function flattenNested(items: unknown[]): unknown[] {
  const out: unknown[] = [];
  for (const item of items) {
    if (!isRecord(item)) {
      out.push(item);
      continue;
    }
    const nested = item.messages ?? item.history ?? item.chats;
    if (Array.isArray(nested) && nested.length) {
      out.push(...nested);
    } else {
      out.push(item);
    }
  }
  return out;
}

/** Map GET /service_desk/chat/ payload into the existing chat bubbles. */
export function parseChatHistory(data: unknown): ParsedChatEntry[] {
  const entries: ParsedChatEntry[] = [];

  unwrapList(data).forEach((item, index) => {
    if (!isRecord(item)) return;

    const role = roleOf(item);
    const namedUserText = pickString(item, [
      "message",
      "query",
      "prompt",
      "user_message",
      "question",
      "text",
      "content",
    ]);
    const botLines = replyLinesOf(item);
    const tools = toolsOf(item);
    const timestamp = pickDate(item);
    const baseId = pickId(item, `hist-${index}`);
    // Live GET stores the user prompt in reply / reply_lines, not message.
    const userText = namedUserText || (role === "user" ? botLines.join("\n") : "");

    if (role === "user" && userText) {
      entries.push({ id: `${baseId}-user`, role: "user", text: userText, timestamp });
      return;
    }

    if (role === "bot" && (botLines.length || namedUserText)) {
      entries.push({
        id: `${baseId}-bot`,
        role: "bot",
        reply_lines: botLines.length ? botLines : [namedUserText],
        tools_used: tools,
        timestamp,
      });
      return;
    }

    if (namedUserText) {
      entries.push({ id: `${baseId}-user`, role: "user", text: namedUserText, timestamp });
    }
    if (botLines.length && role !== "user") {
      entries.push({
        id: `${baseId}-bot`,
        role: "bot",
        reply_lines: botLines,
        tools_used: tools,
        timestamp,
      });
    }
  });

  return entries;
}
